import React from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, Check } from "lucide-react";
import { getBlog, getRelated } from "../data/blogs";
import { PRODUCT } from "../mock";

const Block = ({ block }) => (
  <div className="mb-8">
    {block.h && (
      <h2 className="font-serif-display text-[26px] md:text-[32px] font-medium mb-4 mt-2">{block.h}</h2>
    )}
    {block.p &&
      block.p.map((para, i) => (
        <p key={i} className="text-[16px] md:text-[17px] leading-[1.8] text-[#1A1A1A]/80 mb-4">
          {para}
        </p>
      ))}
    {block.ul && (
      <ul className="space-y-3 my-5">
        {block.ul.map((item, i) => (
          <li key={i} className="flex items-start gap-3 text-[16px] leading-relaxed text-[#1A1A1A]/80">
            <Check size={18} className="text-[#6D28D9] flex-shrink-0 mt-1" />
            {item}
          </li>
        ))}
      </ul>
    )}
  </div>
);

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const blog = getBlog(slug);

  if (!blog) {
    return (
      <main className="min-h-[60vh] flex flex-col items-center justify-center bg-[#F4EFE8] px-6 text-center">
        <h1 className="font-serif-display text-4xl mb-4">Article not found</h1>
        <Link to="/blog" className="btn-lux px-8 py-3 text-[12px] tracking-[0.2em] uppercase">Back to Blogs</Link>
      </main>
    );
  }

  const related = getRelated(slug, 3);

  return (
    <main className="bg-[#F4EFE8]">
      {/* HERO */}
      <section className="bg-[#EDE6DC] pt-12 pb-0">
        <div className="max-w-[820px] mx-auto px-5 md:px-8">
          <button onClick={() => navigate("/blog")} className="flex items-center gap-2 text-[12px] tracking-[0.16em] uppercase text-[#1A1A1A]/60 hover:text-[#1A1A1A] mb-8">
            <ArrowLeft size={15} /> All Blogs
          </button>
          <span className="inline-block text-[11px] tracking-[0.18em] uppercase text-[#6D28D9] font-semibold mb-4">{blog.category}</span>
          <h1 className="font-serif-display text-[34px] md:text-[52px] leading-[1.05] font-medium mb-6">{blog.title}</h1>
          <div className="flex items-center gap-4 text-[13px] text-[#1A1A1A]/55 pb-10">
            <span>{blog.author}</span>
            <span>&bull;</span>
            <span>{blog.date}</span>
            <span className="flex items-center gap-1"><Clock size={13} /> {blog.readTime}</span>
          </div>
        </div>
      </section>

      {/* COVER */}
      <div className="max-w-[980px] mx-auto px-5 md:px-8 -mt-0">
        <img src={blog.image} alt={blog.title} className="w-full h-[320px] md:h-[460px] object-cover rounded-md -translate-y-8 shadow-[0_30px_60px_-30px_rgba(26,26,26,0.4)]" />
      </div>

      {/* BODY */}
      <article className="max-w-[760px] mx-auto px-5 md:px-8 pb-12">
        <p className="font-serif-display text-[20px] md:text-[24px] italic leading-relaxed text-[#1A1A1A]/75 mb-10 pb-10 border-b border-[#1A1A1A]/10">
          {blog.excerpt}
        </p>
        {blog.content.map((block, i) => (
          <Block key={i} block={block} />
        ))}

        {/* CTA */}
        <div className="mt-12 bg-[#1A1A1A] text-[#F4EFE8] rounded-md p-8 md:p-10 text-center">
          <p className="text-[11px] tracking-[0.24em] uppercase text-[#F4EFE8]/60 mb-3">VC-Backed • Built by 4 Dermatologists</p>
          <h3 className="font-serif-display text-[28px] md:text-[36px] font-medium mb-4">Ready to start your glow up?</h3>
          <p className="text-[14px] text-[#F4EFE8]/70 max-w-md mx-auto mb-7">
            Experience the first AI-enhanced red light therapy mask — clinical-grade results, an affordable ${PRODUCT.price}.
          </p>
          <Link to={`/products/${PRODUCT.handle}`} className="btn-lux-light inline-flex px-9 py-4 text-[12px] tracking-[0.22em] uppercase gap-2">
            Shop the Mask <ArrowRight size={15} />
          </Link>
        </div>
      </article>

      {/* RELATED */}
      <section className="max-w-[1280px] mx-auto px-5 md:px-8 py-16 border-t border-[#1A1A1A]/10">
        <h3 className="font-serif-display text-[28px] md:text-[36px] font-medium mb-10 text-center">Keep Reading</h3>
        <div className="grid sm:grid-cols-3 gap-8">
          {related.map((b) => (
            <Link key={b.slug} to={`/blog/${b.slug}`} className="group flex flex-col bg-white rounded-md overflow-hidden border border-[#1A1A1A]/10 hover:shadow-[0_24px_50px_-30px_rgba(26,26,26,0.4)] transition-shadow duration-500">
              <div className="overflow-hidden">
                <img src={b.image} alt={b.title} className="w-full h-44 object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-6">
                <span className="text-[10px] tracking-[0.18em] uppercase text-[#6D28D9] font-semibold">{b.category}</span>
                <h4 className="font-serif-display text-[20px] leading-snug font-medium mt-2 group-hover:text-[#6D28D9] transition-colors">{b.title}</h4>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </main>
  );
};

export default BlogPost;
